import type { DeepPartial } from '../types';
import { merge } from 'es-toolkit/compat';
import { darken, lighten, transparentize } from 'polished';
import { baseTheme } from './base';

/**
 * Theme override accepted by `createTheme` and `<TThemeProvider>`.
 *
 * Provide a partial theme — only the keys you want to change. The brand color seed
 * regenerates the full 12-step ramp (plus the 12-step alpha twin) automatically.
 *
 * ## Two accepted shapes
 *
 * ```ts
 * // Canonical — matches the `color.brand.*` token vocabulary:
 * createTheme({ colors: { brand: { base: '#FF6600' } } });
 *
 * // Legacy — kept working for drop-in compatibility with pre-migration consumers:
 * createTheme({ colors: { primary: { main: '#FF6600' } } });
 * ```
 *
 * When both are passed, `colors.brand.base` wins. Prefer the canonical shape in new code.
 *
 * ## Multi-tenant usage
 *
 * Pass the tenant's brand hex per request to retheme an entire subtree. The brand ramp
 * regenerates from the hex; every semantic token that references `color.brand.*` re-resolves
 * via CSS-variable cascade — no Vue re-renders.
 *
 * ```vue
 * <TThemeProvider :theme="{ colors: { brand: { base: tenantBrandHex } } }">
 *   <App />
 * </TThemeProvider>
 * ```
 */
export type TThemeOverride = DeepPartial<typeof baseTheme> & {
  colors?: {
    /**
     * The tenant brand color seed.
     *
     * Pass a single hex string — `createTheme` derives the 12-step ramp
     * (`--color-brand-1` through `--color-brand-12`) plus the 12-step alpha twin
     * (`--color-brand-a-1` through `--color-brand-a-12`) automatically.
     *
     * Step 9 anchors to the input exact, so primary buttons and headers render the
     * tenant color literally — the rest of the ramp is derived around it for hover
     * states, subtle backgrounds, focus rings, etc.
     */
    brand?: { base?: string };
  };
};

function generateBrandRamp(main: string): Record<number, string> {
  return {
    1: lighten(0.35, main),
    2: lighten(0.30, main),
    3: lighten(0.25, main),
    4: lighten(0.20, main),
    5: lighten(0.15, main),
    6: lighten(0.12, main),
    7: lighten(0.08, main),
    8: lighten(0.04, main), // Per Radix: border-strong, lighter than the anchor.
    9: main,
    10: darken(0.08, main), // Legacy `primary.toku` — solid hover; darkens for parity with other button variants.
    11: darken(0.10, main),
    12: darken(0.15, main),
  };
}

// Step 1 (faintest) → step 12 (strongest). Stored as target opacity; transparentize takes the inverse.
const BRAND_ALPHA_OPACITIES = [0.03, 0.05, 0.10, 0.15, 0.20, 0.30, 0.40, 0.50, 0.60, 0.70, 0.80, 0.90];

/**
 * Builds a theme object from a partial override.
 *
 * Returns the merged theme (drop-in compatible with the legacy theme shape via
 * `useTheme()`) plus a `cssVars` record suitable for inline `style` injection by
 * `<TThemeProvider>`. The cssVars contain the 12-step brand ramp + 12-step alpha twin
 * regenerated from the seed hex; they only exist when a brand override was provided.
 *
 * Typically you don't call this directly — pass the override prop to `<TThemeProvider>`
 * which calls `createTheme` internally. Call it directly only when you need the resolved
 * theme object outside the Vue component tree (e.g. for SSR or for non-Vue consumers).
 *
 * @example
 * ```ts
 * const theme = createTheme({ colors: { brand: { base: '#FF6600' } } });
 *
 * theme.colors.primary.main;  // '#FF6600' (legacy key, still populated)
 * theme.cssVars;              // { '--color-brand-9': '#FF6600', ... }
 * ```
 */
export function createTheme(theme: TThemeOverride = {}) {
  // Clone into a fresh object — `merge` mutates its first arg, which would otherwise
  // corrupt the baseTheme singleton across providers.
  const mergedTheme = merge({}, baseTheme, theme) as typeof baseTheme;
  const main = theme.colors?.brand?.base ?? theme.colors?.primary?.main;

  let cssVars: Record<string, string> | undefined;

  if (main) {
    const ramp = generateBrandRamp(main);

    // Mirror seed into legacy `primary.main` for consumers reading the old shape.
    mergedTheme.colors.primary.main = main;
    mergedTheme.colors.primary.ultraDark = ramp[12];
    mergedTheme.colors.primary.dark = main;
    mergedTheme.colors.primary.toku = ramp[10];
    mergedTheme.colors.primary.light1 = ramp[7];
    mergedTheme.colors.primary.light2 = ramp[4];
    mergedTheme.colors.primary.light3 = ramp[3];
    mergedTheme.colors.primary.light4 = ramp[2];
    mergedTheme.colors.primary.light5 = ramp[1];

    cssVars = {};
    for (const [step, hex] of Object.entries(ramp)) {
      cssVars[`--color-brand-${step}`] = hex;
    }
    for (let i = 1; i <= 12; i++) {
      cssVars[`--color-brand-a-${i}`] = transparentize(1 - BRAND_ALPHA_OPACITIES[i - 1], main);
    }
  }

  return Object.assign(mergedTheme, { cssVars });
}
